import React, { useEffect, useMemo, useState } from "react";
import { FaLayerGroup, FaSave, FaTractor } from "react-icons/fa";

import {
  createMachineGroupId,
  loadServiceOrderMachineGroups,
  saveServiceOrderMachineGroups,
} from "./serviceOrderMachineGroups";

function machineIdentsFromGroup(group) {
  return (group && group.machineIdents ? group.machineIdents : [])
    .map((ident) => String(ident || "").trim())
    .filter(Boolean);
}

function ServiceOrderMachineSelector({
  farmId,
  machines,
  selectedMachineIdents,
  onSelectedMachineIdentsChange,
  requireMachine,
  error,
  t,
}) {
  const [customGroups, setCustomGroups] = useState(() =>
    loadServiceOrderMachineGroups(farmId)
  );
  const [newGroupName, setNewGroupName] = useState("");
  const [showSaveGroup, setShowSaveGroup] = useState(false);

  useEffect(() => {
    setCustomGroups(loadServiceOrderMachineGroups(farmId));
    setShowSaveGroup(false);
    setNewGroupName("");
  }, [farmId]);

  const sortedMachines = useMemo(
    () =>
      (machines || [])
        .slice()
        .sort((left, right) =>
          String(left.ident || "").localeCompare(
            String(right.ident || ""),
            "pt-BR"
          )
        ),
    [machines]
  );

  const allMachineIdents = useMemo(
    () =>
      sortedMachines
        .map((machine) => String(machine.ident || "").trim())
        .filter(Boolean),
    [sortedMachines]
  );

  const selectedSet = useMemo(
    () => new Set(selectedMachineIdents),
    [selectedMachineIdents]
  );

  const persistGroups = (groups) => {
    setCustomGroups(groups);
    saveServiceOrderMachineGroups(farmId, groups);
  };

  const setSelection = (idents) => {
    const next = Array.from(
      new Set(
        (idents || [])
          .map((ident) => String(ident || "").trim())
          .filter(Boolean)
      )
    );
    onSelectedMachineIdentsChange(next);
  };

  const toggleMachine = (ident) => {
    const normalized = String(ident || "").trim();
    if (!normalized) return;
    if (selectedSet.has(normalized)) {
      setSelection(selectedMachineIdents.filter((item) => item !== normalized));
      return;
    }
    setSelection([...selectedMachineIdents, normalized]);
  };

  const handleSaveGroup = () => {
    const name = newGroupName.trim();
    if (!name) return;
    if (selectedMachineIdents.length === 0) return;

    const nextGroups = [
      ...customGroups,
      {
        id: createMachineGroupId(),
        name,
        machineIdents: selectedMachineIdents.slice(),
      },
    ];
    persistGroups(nextGroups);
    setNewGroupName("");
    setShowSaveGroup(false);
  };

  const applyCustomGroup = (group) => {
    setSelection(machineIdentsFromGroup(group));
  };

  const removeCustomGroup = (groupId) => {
    persistGroups(customGroups.filter((group) => group.id !== groupId));
  };

  return (
    <div
      className={`service-order-machine-selector${
        error ? " service-order-machine-selector--error" : ""
      }`}
    >
      <div className="service-order-machine-selector__toolbar">
        <button
          type="button"
          className="btn btn-default btn-xs"
          onClick={() => setSelection(allMachineIdents)}
          disabled={allMachineIdents.length === 0}
        >
          {t("Marcar todas")}
        </button>
        <button
          type="button"
          className="btn btn-default btn-xs"
          onClick={() => setSelection([])}
          disabled={selectedMachineIdents.length === 0}
        >
          {t("Limpar seleção")}
        </button>
        <button
          type="button"
          className="btn btn-default btn-xs"
          onClick={() => setShowSaveGroup((current) => !current)}
          disabled={selectedMachineIdents.length === 0}
        >
          <FaSave style={{ marginRight: 4 }} />
          {t("Salvar grupo")}
        </button>
      </div>

      {selectedMachineIdents.length > 0 ? (
        <p className="service-order-machine-selector__summary">
          {selectedMachineIdents.length}{" "}
          {t("máquina(s) selecionada(s)")}: {selectedMachineIdents.join(", ")}
        </p>
      ) : (
        <p className="service-order-machine-selector__summary is-empty">
          {requireMachine
            ? t("Selecione ao menos uma máquina.")
            : t("Nenhuma máquina selecionada (qualquer equipamento).")}
        </p>
      )}

      {showSaveGroup ? (
        <div className="service-order-machine-selector__save-group">
          <input
            type="text"
            className="form-control input-sm"
            value={newGroupName}
            placeholder={t("Nome do grupo (ex.: Pulverização, Plantio)")}
            onChange={(event) => setNewGroupName(event.target.value)}
          />
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={handleSaveGroup}
            disabled={!newGroupName.trim()}
          >
            {t("Salvar")}
          </button>
          <button
            type="button"
            className="btn btn-default btn-sm"
            onClick={() => {
              setShowSaveGroup(false);
              setNewGroupName("");
            }}
          >
            {t("Cancelar")}
          </button>
        </div>
      ) : null}

      {customGroups.length > 0 ? (
        <div className="service-order-machine-selector__custom-groups">
          <strong>
            <FaLayerGroup style={{ marginRight: 6 }} />
            {t("Meus grupos")}
          </strong>
          <div className="service-order-machine-selector__custom-groups-list">
            {customGroups.map((group) => (
              <div key={group.id} className="service-order-machine-custom-group">
                <button
                  type="button"
                  className="btn btn-default btn-xs"
                  onClick={() => applyCustomGroup(group)}
                  title={machineIdentsFromGroup(group).join(", ")}
                >
                  {group.name} ({machineIdentsFromGroup(group).length})
                </button>
                <button
                  type="button"
                  className="btn btn-link btn-xs service-order-machine-custom-group__remove"
                  onClick={() => removeCustomGroup(group.id)}
                >
                  {t("Remover")}
                </button>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {sortedMachines.length === 0 ? (
        <p className="create-schedule-empty-operators">
          {t("Nenhuma máquina cadastrada nesta fazenda.")}
        </p>
      ) : (
        <div className="service-order-machine-subgroup__items">
          {sortedMachines.map((machine) => (
            <label
              key={machine.id}
              className="create-schedule-operator-item service-order-machine-item"
            >
              <input
                type="checkbox"
                checked={selectedSet.has(String(machine.ident || "").trim())}
                onChange={() => toggleMachine(machine.ident)}
              />
              <span>
                <FaTractor
                  style={{ marginRight: 6, opacity: 0.7 }}
                  aria-hidden
                />
                {machine.ident}
                {machine.operator_label ? ` — ${machine.operator_label}` : ""}
              </span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

export default ServiceOrderMachineSelector;
